import { PageLoader, Notifier, notify } from '@scholastic/volume-react';
import React, { useState, useContext, useEffect } from 'react';
import { CSVLink } from 'react-csv';
import { Redirect } from 'react-router-dom';

import { fetchChartData } from '../../../state/ReportsChart.api';
import { ActionType, StoreContext } from '../../../state/store';

import { SearchableDropdownValue } from '../../Home/Home.dataparser';
import { sumData, SummedData, GetNeededValueForView } from '../OnScreenReport.utils';
import { TABLE_HEADERS } from './OverallSummaryReport.data';

import './OverallSummaryReport.scss';

export interface AvailabeSites {
  group: string;
  label: string;
  value: string;
}

const OverallSummaryReport = () => {
  const [dataIsReady, setDataisReady] = useState<boolean>(false);
  const [tableView, setTableView] = useState<any>();

  const [dataForDownload, setDataForDownload] = useState<any>([]);

  const { state, dispatch } = useContext(StoreContext);
  const reportParams = state.reportInfo;
  const customerInfo = state.customerInfo;

  useEffect(() => {
    const getSummary = async () => {
      const requests = reportParams.products.map((product: SearchableDropdownValue) =>
        fetchChartData({
          request: 'usageSummary',
          cuid: reportParams.cuId,
          startDate: reportParams.startDate,
          endDate: reportParams.endDate,
          product: [product.value],
          customer: reportParams.customer,
        }),
      );

      try {
        const dataForIndividualProducts = await Promise.all(requests);

        generateView(
          dataForIndividualProducts.map((data, i) => ({
            product: reportParams.products[i].label,
            // @ts-ignore
            data: sumData(data.result),
          })),
        );

        generateCSV(
          dataForIndividualProducts.map((data, i) => ({
            product: reportParams.products[i].label,
            // @ts-ignore
            data: sumData(data.result, true),
          })),
        );
      } catch (err) {
        notify.warning('Something went wrong, please try again later');
        console.error(err);
      }
    };

    if (reportParams && customerInfo) {
      getSummary();
    }

    // eslint-disable-next-line
  }, [reportParams, customerInfo]);

  const generateView = (allProductsData: Array<{ product: string; data: SummedData }>) => {
    const tableView = [];
    const intl = new Intl.NumberFormat();

    for (let i = 0; i < allProductsData.length; i++) {
      tableView.push(
        <tr>
          <td>{allProductsData[i]?.product}</td>
          <td>{intl.format(allProductsData[i]?.data.onsite.documents)}</td>
          <td>{intl.format(allProductsData[i]?.data.onsite.sessions)}</td>
          <td>{GetNeededValueForView(allProductsData[i]?.data.onsite.searches)}</td>
          <td>{intl.format(allProductsData[i]?.data.remote.documents)}</td>
          <td>{intl.format(allProductsData[i]?.data.remote.sessions)}</td>
          <td>{GetNeededValueForView(allProductsData[i]?.data.remote.searches)}</td>
          <td>{intl.format(allProductsData[i]?.data.total.documents)}</td>
          <td>{intl.format(allProductsData[i]?.data.total.sessions)}</td>
          <td>{GetNeededValueForView(allProductsData[i]?.data.total.searches)}</td>
        </tr>,
      );
    }

    setTableView(tableView);
    setDataisReady(true);
  };

  const generateCSV = (allProductsData: Array<{ product: string; data: SummedData }>) => {
    const intl = new Intl.NumberFormat();
    const dataForDownload = [];

    for (let i = 0; i < allProductsData.length; i++) {
      const individualDocuments = allProductsData[i]?.data.individual
        ? allProductsData[i]?.data.individual?.documents
        : 0;
      const individualSessions = allProductsData[i]?.data.individual
        ? allProductsData[i]?.data.individual?.sessions
        : 0;
      const individualSearches =
        typeof allProductsData[i]?.data.individual !== 'undefined'
          ? allProductsData[i]?.data.individual?.searches
          : 'N/A';

      dataForDownload.push({
        name: customerInfo.result.customername,
        product: allProductsData[i]?.product,
        onsiteDocuments: intl.format(allProductsData[i]?.data.onsite.documents),
        onsiteSessions: intl.format(allProductsData[i]?.data.onsite.sessions),
        onsiteSearches: GetNeededValueForView(allProductsData[i]?.data.onsite.searches),
        remoteDocuments: intl.format(allProductsData[i]?.data.remote.documents),
        remoteSessions: intl.format(allProductsData[i]?.data.remote.sessions),
        remoteSearches: GetNeededValueForView(allProductsData[i]?.data.remote.searches),
        individualDocuments: intl.format(individualDocuments || 0),
        individualSessions: intl.format(individualSessions || 0),
        individualSearches: GetNeededValueForView(
          typeof individualSearches === 'number' ? individualSearches : 'N/A',
        ),
        totalDocuments: intl.format(allProductsData[i]?.data.total.documents),
        totalSessions: intl.format(allProductsData[i]?.data.total.sessions),
        totalSearches: GetNeededValueForView(allProductsData[i]?.data.total.searches),
      });
    }

    setDataForDownload(dataForDownload);
    setDataisReady(true);

    if (state.reportInfo.outputType === 'Csv') {
      setTimeout(() => {
        let element: HTMLElement = document.querySelector(
          '.stats-o-sum-r__download-link',
        ) as HTMLElement;

        if (element) {
          element.click();
        }
      }, 0);
    }
  };

  const backToHome = () => {
    dispatch({ type: ActionType.SET_REPORT_INFO, data: null });
  };

  const print = () => {
    window.print();
  };

  if (!state.reportInfo) return <Redirect to="/home" />;

  return (
    <div className="stats-o-sum-r">
      <Notifier
        autoClose={5000}
        closeButton={false}
        closeOnClick={false}
        draggable
        draggablePercent={50}
        limit={1}
        newestOnTop
        pauseOnFocusLoss
        pauseOnHover
        position="top-center"
        role="alert"
      />

      {!dataIsReady && (
        <div>
          <PageLoader color={'dark'} size={'large'} type={'border'} />
        </div>
      )}
      {dataIsReady && (
        <div className="stats-o-sum-r__buttons">
          {state.reportInfo.outputType !== 'Csv' && (
            <>
              <CSVLink
                className="stats-o-sum-r__download-link float-right mt-2 vol-button vol-button--info"
                data={dataForDownload}
                headers={TABLE_HEADERS}
                filename={'summary-report.csv'}
              >
                {' '}
                Download CSV{' '}
              </CSVLink>
              <span
                className="stats-o-sum-r__link float-right mt-2 vol-button vol-button--info"
                onClick={print}
                id="print-report"
              >
                {' '}
                Print{' '}
              </span>
            </>
          )}
          <span
            className="stats-o-sum-r__link float-left mt-2 vol-button vol-button--info"
            id="navigate-away"
            onClick={backToHome}
          >
            {' '}
            Back To Report Selector{' '}
          </span>
          <div className="stats-o-sum-r__clearfix"></div>
        </div>
      )}
      {dataIsReady && state.reportInfo.outputType === 'Html' && (
        <div className="stats-o-sum-r__table-holder" id="section-to-print">
          <div className="stats-o-sum-r__table-title"> Overall Summary Report </div>
          <div className="stats-o-sum-r__table-title">
            {' '}
            {reportParams?.loggedInUserName} : {reportParams?.startDate} / {reportParams?.endDate}{' '}
          </div>
          <div className="stats-o-sum-r__table">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th colSpan={3}>On-Site</th>
                  <th colSpan={3}>Remote</th>
                  <th colSpan={3}>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td></td>
                  <td>Documents</td>
                  <td>Sessions</td>
                  <td>Searches</td>
                  <td>Documents</td>
                  <td>Sessions</td>
                  <td>Searches</td>
                  <td>Documents</td>
                  <td>Sessions</td>
                  <td>Searches</td>
                </tr>
                {tableView}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {dataIsReady && state.reportInfo.outputType === 'Csv' && (
        <div>
          If the download doesn't start automatically please,{' '}
          <CSVLink
            className="stats-o-sum-r__download-link stats-o-sum-r__download-link--link-text"
            data={dataForDownload}
            headers={TABLE_HEADERS}
            filename={'summary-report.csv'}
          >
            {' '}
            Click Here{' '}
          </CSVLink>
        </div>
      )}
    </div>
  );
};

export default OverallSummaryReport;
